import {threads} from 'wasm-feature-detect';
import {WonderlandEngine} from './engine.js';
import {XROfferSessionOptions} from './webxr.js';
import {LogLevel} from './utils/logger.js';
import {WASM} from './wasm.js';

import {getBaseUrl} from './utils/fetch.js';

export * from './utils/index.js';
export * from './decorators.js';
export {NativeComponents} from './component.js';
export * from './wonderland.js';
export * from './engine.js';
export * from './webxr.js';
export * from './property.js';
export * from './prefab.js';
export * from './scene.js';
export * from './scene-gltf.js';
export * from './resources/resource.js';
export * from './resources/material-manager.js';
export * from './resources/mesh-manager.js';
export {TextureManager} from './resources/texture-manager.js';
export * from './types.js';
export * from './version.js';
export * from './wasm.js';

/** Relative default path for the loading screen. */
const LOADING_SCREEN_PATH = 'WonderlandRuntime-LoadingScreen.bin';

function loadScript(scriptURL: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        const s = document.createElement('script');
        const node = document.body.appendChild(s);
        s.onload = () => {
            document.body.removeChild(node);
            resolve();
        };
        s.onerror = (e) => {
            document.body.removeChild(node);
            reject(e);
        };
        s.src = scriptURL;
    });
}

/**
 * Checks for support of WebAssembly features.
 *
 * @returns An object containing booleans for supported features.
 */
async function detectFeatures(): Promise<{
    threadsSupported: boolean;
}> {
    let threadsSupported = await threads();
    if (threadsSupported) {
        if (self.crossOriginIsolated) {
            console.log('WASM Threads is supported');
        } else {
            console.warn(
                'WASM Threads is supported, but the page is not crossOriginIsolated, therefore thread support is disabled.'
            );
        }
    } else {
        console.warn('WASM Threads is not supported');
    }

    threadsSupported = threadsSupported && self.crossOriginIsolated;
    return {
        threadsSupported,
    };
}

/**
 * Options to forward to {@link loadRuntime}
 */
export interface LoadRuntimeOptions {
    /**
     * @deprecated Ignored, SIMD in the runtime is always enabled as it's supported on
     * all modern browsers.
     */
    simd: boolean;
    /**
     * If `true`, forces the runtime to load the threads-compatible version.
     * If `undefined`, performs browser feature detection to check whether threads are supported or not.
     */
    threads: boolean;
    /**
     * If `true`, forces the runtime to load the webgpu-compatible version.
     */
    webgpu: boolean;
    /**
     * If `true`, forces the runtime to load a physx-compatible version.
     *
     * **Note**: If your scene uses physx, you **must** enable this option.
     */
    physx: boolean;
    /**
     * If `true`, forces the runtime to load a loader-compatible version.
     *
     * This option allows to load gltf data at runtime.
     */
    loader: boolean;

    /**
     * Whether to instantiate the runtime with a renderer.
     *
     * Disabling this is mostly meant for testing purposes (increased speed).
     *
     * @hidden
     */
    renderer: boolean;

    /**
     * Path to the loading screen. If `undefined`, defaults to 'WonderlandRuntime-LoadingScreen.bin'.
     * Beware that these are special .bin files signed by Wonderland. Customizing
     * requires an enterprise license, please reach out for more information.
     */
    loadingScreen: string;

    /**
     * Default framebuffer scale factor. This can later be changed using
     * {@link WonderlandEngine.xrFramebufferScaleFactor}
     */
    xrFramebufferScaleFactor: number;

    /**
     * Whether to advertise AR/VR session support to the browser.
     *
     * Adds an interactive UI element to the browser interface to start an XR
     * session. Browser support is optional, so it's advised to still allow
     * requesting a session with a UI element on the website itself.
     *
     * If `undefined`, no XR session is automatically offered to the browser.
     *
     * @since 1.1.5
     */
    xrOfferSession: XROfferSessionOptions;

    /**
     * Canvas id or element. If this is `undefined`, looks for a canvas with id 'canvas'.
     */
    canvas: string;

    /**
     * Logging level(s) to enable.
     *
     * By default, all levels are enabled.
     */
    logs: LogLevel[];
}

/**
 * Load the runtime using the WASM and JS files.
 *
 * @param runtime The runtime base string, e.g,: `WonderlandRuntime-loader-physx`.
 * @param options Options to modify the loading behaviour.
 *
 * @returns A promise that resolves when the engine is ready to be used.
 */
export async function loadRuntime(
    runtime: string,
    options: Partial<LoadRuntimeOptions> = {}
): Promise<WonderlandEngine> {
    const baseURL = getBaseUrl(runtime);

    const {threadsSupported} = await detectFeatures();
    const {
        threads = threadsSupported,
        webgpu = false,
        physx = false,
        loader = false,
        renderer = true,
        xrFramebufferScaleFactor = 1.0,
        xrOfferSession = null,
        loadingScreen = baseURL ? `${baseURL}/${LOADING_SCREEN_PATH}` : LOADING_SCREEN_PATH,
        canvas = 'canvas',
        logs = [LogLevel.Info, LogLevel.Warn, LogLevel.Error],
    } = options;

    const variant = [];
    if (loader) variant.push('loader');
    if (physx) variant.push('physx');
    if (threads) variant.push('threads');
    if (webgpu) variant.push('webgpu');

    const variantStr = variant.join('-');

    let filename = runtime;
    if (variantStr) filename = `${filename}-${variantStr}`;

    const download = function (
        filename: string,
        errorMessage: string
    ): Promise<ArrayBuffer> {
        return fetch(filename)
            .then((r) => {
                if (!r.ok) return Promise.reject(errorMessage);
                return r.arrayBuffer();
            })
            .catch((_) => Promise.reject(errorMessage));
    };

    const [wasmData, loadingScreenData] = await Promise.all([
        download(`${filename}.wasm`, `Failed to fetch runtime .wasm file: ${filename}`),
        download(loadingScreen, 'Failed to fetch loading screen file'),
    ]);

    const canvasElement = document.getElementById(canvas) as HTMLCanvasElement;
    if (!canvasElement) {
        throw new Error(`loadRuntime(): Failed to find canvas with id '${canvas}'`);
    }
    if (!(canvasElement instanceof HTMLCanvasElement)) {
        throw new Error(`loadRuntime(): HTML element '${canvas}' must be a canvas`);
    }

    const wasm = new WASM(threads);
    (wasm.wasm as ArrayBuffer) = wasmData;
    (wasm.canvas as HTMLCanvasElement) = canvasElement;
    wasm._log.levels.enable(...logs);

    if (!window._WL) {
        window._WL = {runtimes: {}};
    }
    const runtimes = window._WL.runtimes;

    /* Global identifier of this runtime in `window`. */
    const runtimeGlobalId = variantStr ? variantStr : 'default';
    /* Only load the runtime if not previously loaded in the page */
    if (!runtimes[runtimeGlobalId]) {
        await loadScript(`${filename}.js`);
        runtimes[runtimeGlobalId] = window.instantiateWonderlandRuntime!;
        window.instantiateWonderlandRuntime = undefined;
    }
    await runtimes[runtimeGlobalId](wasm);

    if (webgpu) {
        const WebGPU = (wasm as any).WebGPU;
        const adapter = await (navigator as any).gpu.requestAdapter();
        const adapterId = WebGPU.mgrAdapter.create(adapter);
        const desc = {
            requiredFeatures: ['texture-compression-bc', 'depth32float-stencil8'],
        };
        const device = await adapter.requestDevice(desc);
        const deviceId = WebGPU.mgrDevice.create(adapter);
        (wasm.preinitializedWebGPUDevice as any) = device;

        const context = canvasElement.getContext('webgpu')! as any;

        context.configure({
            device: device,
            format: (navigator as any).gpu.getPreferredCanvasFormat(),
            alphaMode: 'premultiplied',
        });
    }

    const engine = new WonderlandEngine(wasm, loadingScreenData, renderer);

    await engine.webxr.init(xrFramebufferScaleFactor, xrOfferSession);

    engine.autoResizeCanvas = true;
    engine.start();

    return engine;
}
